import React, { useEffect, useState } from "react"
import Helmet from "react-helmet"
import { BLOCKS } from "@contentful/rich-text-types"

import { Button, Input, ListBox, RichText, Textarea } from "components"

const categories = [
  {
    label: "Accounting/Finance",
    value: "Accounting/Finance",
  },
  {
    label: "Administrative Support",
    value: "Administrative Support",
  },
  {
    label: "Customer Service",
    value: "Customer Service",
  },
  {
    label: "Engineering",
    value: "Engineering",
  },
  {
    label: "Human Resources",
    value: "Human Resources",
  },
  {
    label: "Maintenance",
    value: "Maintenance",
  },
  {
    label: "Plant Management",
    value: "Plant Management",
  },
  {
    label: "Production/Production Support",
    value: "Production/Production Support",
  },
  {
    label: "Quality",
    value: "Quality",
  },
  {
    label: "Safety/EHS",
    value: "Safety/EHS",
  },
  {
    label: "Sanitation",
    value: "Sanitation",
  },
  {
    label: "Warehouse/Logistics",
    value: "Warehouse/Logistics",
  },
]

function GeneralInquiry({ content, secondaryContent }) {
  const [businessName, setBusinessName] = useState("")
  const [category, setCategory] = useState("placeholder")
  const [categoryError, setCategoryError] = useState("")
  const [details, setDetails] = useState("")
  const [detailsError, setDetailsError] = useState("")

  const [firstName, setFirstName] = useState("")
  const [firstNameError, setFirstNameError] = useState("")
  const [lastName, setLastName] = useState("")
  const [lastNameError, setLastNameError] = useState("")
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState("")
  const [phone, setPhone] = useState("")
  const [optOut, setOptOut] = useState(false)

  // fix SSR & timestamp interval issues with ReCAPTCHA
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
    function timestamp() {
      var response = document.getElementsByName("g-recaptcha-response")
      if (response[0] == null || response[0].value.trim() == "") {
        var elems = JSON.parse(
          document.getElementsByName("captcha_settings")[0].value
        )
        elems["ts"] = JSON.stringify(new Date().getTime())
        document.getElementsByName(
          "captcha_settings"
        )[0].value = JSON.stringify(elems)
      }
    }
    let interval = setInterval(timestamp, 500)
    return function cleanup() {
      clearInterval(interval)
    }
  }, [])

  return (
    <div className="pb-10 -mt-12 wrapper md:mt-0 md:pb-28">
      {mounted && (
        <Helmet>
          <script
            src={
              "https://www.google.com/recaptcha/api.js?testQuery=" +
              Math.random()
            }
          />
        </Helmet>
      )}
      <div className="default-grid">
        <div className="col-span-full md:col-span-6">
          <div className="relative h-full px-6 py-10 -mx-6 bg-lightBlue md:px-18 sm:-mx-fluidMargin md:mx-0 md:py-14 sm sm:px-fluidMargin">
            <ul className="space-y-12 md:max-w-sm">
              {content && (
                <li className="space-y-6">
                  <RichText
                    content={content}
                    size="small"
                    options={{
                      renderNode: {
                        [BLOCKS.HEADING_5]: (node, children) => (
                          <h3 className="mt-2 mb-2 lead-copy text-darkNavy last:mb-0 first:mt-0">
                            {children}
                          </h3>
                        ),
                      },
                    }}
                  />
                  <div className="w-full h-px bg-violet" />
                </li>
              )}
              {secondaryContent && (
                <li className="space-y-6">
                  <RichText
                    content={secondaryContent}
                    size="small"
                    options={{
                      renderNode: {
                        [BLOCKS.HEADING_5]: (node, children) => (
                          <h3 className="mt-2 mb-2 lead-copy text-darkNavy last:mb-0 first:mt-0">
                            {children}
                          </h3>
                        ),
                      },
                    }}
                  />
                  <div className="w-full h-px bg-violet" />
                </li>
              )}
            </ul>
            <div className="absolute bottom-0 left-0 hidden w-full md:block">
              <div className="absolute top-0 left-0 w-11/12 h-px transform bg-mineralGray -translate-x-14" />
              <div className="absolute top-0 left-0 w-px transform -translate-y-10 bg-mineralGray h-15" />
            </div>
          </div>
        </div>
        <div className="col-span-full md:col-span-6">
          <form
            className="space-y-5"
            action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8"
            method="POST"
            noValidate
            onSubmit={e => {
              let error

              setCategoryError("")
              setDetailsError("")

              setFirstNameError("")
              setLastNameError("")
              setEmailError("")

              // business items
              if (!category || category === "placeholder") {
                setCategoryError("Please choose an option")
                error = true
              }
              if (!details) {
                setDetailsError("Please provide some details")
                error = true
              }

              //contact items
              if (!firstName) {
                setFirstNameError("Please provide your first name")
                error = true
              }
              if (!lastName) {
                setLastNameError("Please provide your last name")
                error = true
              }
              if (!email || !/(.+)@(.+){2,}\.(.+){2,}/.test(email)) {
                setEmailError("Please provide a valid email address")
                error = true
              }
              // if error, prevent default form submission
              if (error) {
                e.preventDefault()
              }
            }}
          >
            <ListBox
              label="Interest/Category"
              placeholder="Select an option"
              options={categories}
              error={categoryError}
              id="00N4x00000Kgrpr"
              name="00N4x00000Kgrpr"
              required
              value={category}
              onChange={value => setCategory(value)}
            />
            <Input
              label="First name"
              value={firstName}
              error={firstNameError}
              id="first_name"
              name="first_name"
              type="text"
              spellCheck="false"
              required
              onChange={e => setFirstName(e.target.value)}
            />
            <Input
              label="Last name"
              value={lastName}
              error={lastNameError}
              id="last_name"
              name="last_name"
              type="text"
              spellCheck="false"
              required
              onChange={e => setLastName(e.target.value)}
            />
            <Input
              label="Business name"
              placeholder="Optional"
              value={businessName}
              id="company"
              name="company"
              type="text"
              spellCheck="false"
              onChange={e => setBusinessName(e.target.value)}
            />
            <Input
              label="Email address"
              value={email}
              error={emailError}
              id="email"
              name="email"
              type="email"
              pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
              spellCheck="false"
              required
              onChange={e => setEmail(e.target.value)}
            />
            <Input
              label="Phone"
              placeholder="Optional"
              value={phone}
              id="phone"
              name="phone"
              type="text"
              pattern="^\+?(?:\(?[0-9]\)?( |-)?){6,14}[0-9]$"
              spellCheck="false"
              onChange={e => setPhone(e.target.value)}
            />
            <Textarea
              label="Additional details"
              placeholder="Describe how we could help your business and/or product"
              value={details}
              error={detailsError}
              id="00N4x00000PsiYa"
              name="00N4x00000PsiYa"
              rows="7"
              required
              onChange={e => setDetails(e.target.value)}
            />
            <div>
              <input
                className="mr-2"
                value={optOut}
                id="emailOptOut"
                name="emailOptOut"
                type="checkbox"
                required
                onClick={() => setOptOut(!optOut)}
              />
              <label
                className="small-copy text-mineralGray"
                htmlFor="emailOptOut"
              >
                I agree to receive marketing emails from Hearthside and
                understand that I can unsubscribe at any time.
              </label>
            </div>
            <div>
              <div
                class="g-recaptcha"
                data-sitekey="6LcbyVQbAAAAADNa7lAKwOZOD-x-TSbmgVK2VGzA"
              ></div>
            </div>
            <Button label="Submit" type="submit" onClick={() => {}} />
            <input
              type="hidden"
              name="captcha_settings"
              value='{"keyname":"websitekey","fallback":"true","orgId":"00D4x0000016btB","ts":""}'
            />
            <input type="hidden" name="oid" value="00D4x0000016btB" />
            <input
              type="hidden"
              name="retURL"
              value="https://www.hearthsidefoods.com/thank-you/"
            />
            <input type="hidden" id="Source__c" name="Source__c" value="info" />
          </form>
        </div>
      </div>
    </div>
  )
}

export default GeneralInquiry
